import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Tabs,
  List,
  Avatar,
  Tag,
  Space,
  Modal,
  message,
  Badge,
  Typography,
  Spin,
  Form,
  Input,
  InputNumber
} from 'antd'
import {
  LogoutOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  CalculatorOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  EyeOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  PrinterOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { formatPeriodLabel, getOutstanding as getOutstandingUtil, getVehicleDueDate, isLeaseStarted, getAdjustmentRevenueDelta } from './vehicleManagerUtils'

const { Text, Title } = Typography

type VoucherLine = {
  summary: string
  subject: string
  debit?: number
  credit?: number
}

type VoucherItem = {
  plate?: string
  amount?: number
  company?: string
  dueDate?: string
  periodKey?: string
  chargeRangeStart?: string
  chargeRangeEnd?: string
}

type VoucherData = {
  voucherNo: string
  date: string
  pageNo?: string
  totalPages?: string
  attachmentCount?: number
  accountant?: string
  bookkeeper?: string
  maker?: string
  reviewer?: string
  cashier?: string
  printDate?: string
  lines: VoucherLine[]
  totalDebit?: number
  totalCredit?: number
  scope?: 'batch' | 'single'
  items?: VoucherItem[]
}

const defaultLine = (): VoucherLine => ({
  summary: '',
  subject: '',
  debit: undefined,
  credit: undefined
})

const AccountantPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('pending')
  const [loading, setLoading] = useState(true)
  const [pendingReceipts, setPendingReceipts] = useState<any[]>([])
  const [completedReceipts, setCompletedReceipts] = useState<any[]>([])
  const [statistics, setStatistics] = useState({
    pendingAccounting: 0,
    completedToday: 0,
    totalAmount: 0
  })
  const [voucherModalVisible, setVoucherModalVisible] = useState(false)
  const [voucherPreviewVisible, setVoucherPreviewVisible] = useState(false)
  const [voucherForm] = Form.useForm()
  const watchedItems = Form.useWatch('items', voucherForm) as VoucherItem[] | undefined
  const [editingReceipt, setEditingReceipt] = useState<any>(null)
  const [previewData, setPreviewData] = useState<VoucherData | null>(null)
  const [allReceipts, setAllReceipts] = useState<any[]>([])
  const [adjustments, setAdjustments] = useState<any[]>([])
  const [expiringModalVisible, setExpiringModalVisible] = useState(false)
  const [rentedExpanded, setRentedExpanded] = useState(false)

  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { vehicles, fetchVehicles } = useVehicleStore()

  useEffect(() => {
    loadData()
  }, [])

  const formatPlate = (plate: any) => {
    if (plate === null || plate === undefined) return ''
    if (typeof plate === 'number') return Number.isFinite(plate) ? plate.toFixed(0) : ''
    return String(plate)
  }

  const getReceiptItems = (receipt: any) => {
    const raw = receipt?.items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
        return []
      }
    }
    return []
  }

  const normalizeReceipts = (list: any[]) =>
    list.flatMap(r => {
      if (r.mergeByCompany) {
        const items = getReceiptItems(r)
        const total = items.reduce((s: number, it: any) => s + (Number(it.expectedAmount ?? it.amount ?? 0) || 0), 0)
        return [{
          ...r,
          receiptId: r._id || r.id,
          receiptStatus: r.status,
          amount: total || Number(r.amount || 0) || 0,
          company: r.company,
          plate: r.plate || `${r.company || '合并收款'}`,
          rawReceipt: r
        }]
      }
      const items = getReceiptItems(r)
      const normalizedItems = items.length ? items : [{ ...r }]
      return normalizedItems.map((it: any, idx: number) => ({
        ...it,
        receiptId: r._id || r.id,
        receiptStatus: r.status,
        itemIndex: idx,
        amount: Number(it.expectedAmount ?? it.amount ?? r.amount ?? 0),
        vehicleId: it.vehicleId || r.vehicleId,
        plate: formatPlate(it.plate || r.plate),
        model: it.model || r.model,
        company: it.company || r.company,
        contactPerson: it.contactPerson || r.contactPerson,
        dueDate: it.dueDate || r.dueDate,
        rentPeriod: it.rentPeriod || r.rentPeriod,
        rawReceipt: r
      }))
    })

  const mapWithVehicle = (list: any[]) =>
    list.map(r => {
      const v = vehicles.find(vh => vh.id === r.vehicleId || vh._id === r.vehicleId || vh.plate === r.plate)
      const reminderDue = v ? getVehicleDueDate(v) : r.dueDate
      return {
        ...r,
        plate: r.plate || v?.plate || '未知车牌',
        model: r.model || v?.type || '',
        company: r.company || v?.company || '',
        contactPerson: r.contactPerson || v?.contactPerson || '',
        amount: r.amount ?? v?.rentAmount ?? 0,
        dueDate: r.dueDate || v?.dueDate,
        reminderDue,
        reminderAmount: v?.reminderAmount ?? v?.rentAmount,
        reminderPeriod: v?.reminderPeriod || v?.rentPeriod,
        reminderPeriodMonths: v?.reminderPeriodMonths,
        voucherData: r.voucherData || r.rawReceipt?.voucherData
      }
    })

  const getPeriodLabel = (value?: string, months?: number) => formatPeriodLabel(value, months)

  const totalOutstanding = useMemo(
    () => vehicles.reduce((sum, v) => sum + getOutstandingUtil(v, allReceipts, adjustments), 0),
    [vehicles, allReceipts, adjustments]
  )

  useEffect(() => {
    localStorage.setItem('jxc_total_outstanding', totalOutstanding.toFixed(2))
  }, [totalOutstanding])

  const getExpiringVehicles = (limitDays?: number) => {
    const now = dayjs()
    return vehicles
      .filter(v => v.status === 'rented')
      .filter(v => getVehicleDueDate(v))
      .filter(v => isLeaseStarted(v, now))
      .filter(v => {
        if (limitDays === undefined) return true
        const diff = dayjs(getVehicleDueDate(v)).diff(now, 'day')
        return diff <= limitDays && diff >= 0
      })
      .sort((a, b) => dayjs(getVehicleDueDate(a)).diff(dayjs(getVehicleDueDate(b))))
  }

  const expiringVehiclesAll = getExpiringVehicles()
  const expiringTop = expiringVehiclesAll.slice(0, 3)

  const rentedVehicles = useMemo(
    () => vehicles.filter(v => v.status === 'rented'),
    [vehicles]
  )

  const rentedWithOutstanding = useMemo(
    () => rentedVehicles
      .map(v => ({ vehicle: v, outstanding: getOutstandingUtil(v, allReceipts, adjustments) }))
      .sort((a, b) => b.outstanding - a.outstanding),
    [rentedVehicles, allReceipts, adjustments]
  )

  const loadData = async () => {
    try {
      setLoading(true)
      await fetchVehicles().catch(() => {})
      const [pendingRes, completedRes, allRes, adjustmentsRes] = await Promise.all([
        simpleDirectCloudApi.getReceipts('cashier_done'),
        simpleDirectCloudApi.getReceipts('accounted'),
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments()
      ])

      const pendingRaw = pendingRes.success && pendingRes.data ? pendingRes.data : []
      const completedRaw = completedRes.success && completedRes.data ? completedRes.data : []
      const allRaw = allRes.success && allRes.data ? allRes.data : []
      const adjustmentsRaw = adjustmentsRes.success && adjustmentsRes.data ? adjustmentsRes.data : []
      const adjustmentsClean = adjustmentsRaw.filter((adj: any) => adj.type !== 'rent_addition')

      const pending = mapWithVehicle(normalizeReceipts(pendingRaw))
      const completed = mapWithVehicle(normalizeReceipts(completedRaw))
      setAllReceipts(allRaw)
      setAdjustments(adjustmentsClean)

      const today = new Date().toDateString()
      const completedToday = completed.filter(r => r.accountedAt && new Date(r.accountedAt).toDateString() === today).length
      const adjustmentsDelta = getAdjustmentRevenueDelta(adjustmentsClean)
      const totalAmount = pending.reduce((s: number, r: any) => s + (Number(r.amount) || 0), 0) + adjustmentsDelta

      setPendingReceipts(pending)
      setCompletedReceipts(completed)
      setStatistics({
        pendingAccounting: pending.length,
        completedToday,
        totalAmount
      })
    } catch (error) {
      console.error(error)
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const openVoucherModal = (receipt: any) => {
    const voucherNo = receipt.voucherNo || `CGZ${Date.now()}`
    const includedItems = pendingReceipts.filter(r => r.receiptId === receipt.receiptId)
    const rawItems = getReceiptItems(receipt.rawReceipt || receipt)
    const detailSource = rawItems.length ? rawItems : includedItems
    const totalAmount = includedItems.reduce((s: number, it: any) => s + (Number(it.amount) || 0), 0)
    const company = receipt.company || includedItems.find(it => it.company)?.company || receipt.customerName || '客户'
    const autoLines: VoucherLine[] = [
      {
        summary: `车辆租金-${company}`,
        subject: '应收账款',
        debit: totalAmount || Number(receipt.amount || 0) || 0,
        credit: undefined
      },
      {
        summary: `车辆租金-${company}`,
        subject: '车辆租金',
        debit: undefined,
        credit: totalAmount || Number(receipt.amount || 0) || 0
      }
    ]
    const lines: VoucherLine[] = receipt.voucherData?.lines?.length
      ? receipt.voucherData.lines
      : autoLines
    const initial: VoucherData = {
      voucherNo,
      date: receipt.voucherData?.date || dayjs().format('YYYY-MM-DD'),
      pageNo: receipt.voucherData?.pageNo || '1',
      totalPages: receipt.voucherData?.totalPages || '1',
      attachmentCount: receipt.voucherData?.attachmentCount || 0,
      accountant: receipt.voucherData?.accountant || user?.nickName || '',
      bookkeeper: receipt.voucherData?.bookkeeper || '',
      maker: receipt.voucherData?.maker || '',
      reviewer: receipt.voucherData?.reviewer || '',
      cashier: receipt.voucherData?.cashier || '',
      printDate: receipt.voucherData?.printDate || dayjs().format('YYYY-MM-DD'),
      lines,
      totalDebit: receipt.voucherData?.totalDebit || lines.reduce((s, l) => s + (Number(l.debit) || 0), 0),
      totalCredit: receipt.voucherData?.totalCredit || lines.reduce((s, l) => s + (Number(l.credit) || 0), 0),
      scope: 'batch',
      items: detailSource.map((it: any) => ({
        plate: formatPlate(it.plate),
        amount: it.amount ?? it.expectedAmount ?? it.receivedAmount,
        company: it.company || receipt.company,
        dueDate: it.dueDate,
        periodKey: it.periodKey,
        chargeRangeStart: it.chargeRangeStart,
        chargeRangeEnd: it.chargeRangeEnd
      }))
    }
    setEditingReceipt(receipt)
    voucherForm.setFieldsValue(initial)
    setVoucherModalVisible(true)
  }

  const buildAutoVoucher = (receipt: any): VoucherData => {
    const voucherNo = receipt.voucherNo || `CGZ${Date.now()}`
    const includedItems = pendingReceipts.filter(r => r.receiptId === receipt.receiptId)
    const rawItems = getReceiptItems(receipt.rawReceipt || receipt)
    const detailSource = rawItems.length ? rawItems : includedItems
    const totalAmount = includedItems.reduce((s: number, it: any) => s + (Number(it.amount) || 0), 0) || Number(receipt.amount || 0) || 0
    const company = receipt.company || includedItems.find(it => it.company)?.company || receipt.customerName || '客户'
    const lines: VoucherLine[] = [
      {
        summary: `车辆租金-${company}`,
        subject: '应收账款',
        debit: totalAmount,
        credit: undefined
      },
      {
        summary: `车辆租金-${company}`,
        subject: '车辆租金',
        debit: undefined,
        credit: totalAmount
      }
    ]
    return {
      voucherNo,
      date: dayjs().format('YYYY-MM-DD'),
      pageNo: '1',
      totalPages: '1',
      attachmentCount: 0,
      accountant: user?.nickName || '',
      bookkeeper: '',
      maker: '',
      reviewer: '',
      cashier: '',
      printDate: dayjs().format('YYYY-MM-DD'),
      lines,
      totalDebit: totalAmount,
      totalCredit: totalAmount,
      scope: 'batch',
      items: detailSource.map((it: any) => ({
        plate: formatPlate(it.plate),
        amount: it.amount ?? it.expectedAmount ?? it.receivedAmount,
        company: it.company || receipt.company,
        dueDate: it.dueDate,
        periodKey: it.periodKey,
        chargeRangeStart: it.chargeRangeStart,
        chargeRangeEnd: it.chargeRangeEnd
      }))
    }
  }

  const handleAutoAccount = async (receipt: any) => {
    try {
      const voucher = buildAutoVoucher(receipt)
      const raw = receipt?.rawReceipt || {}
      const res = await simpleDirectCloudApi.updateReceipt(receipt.receiptId, {
        status: 'accounted',
        voucherData: voucher,
        voucherNo: voucher.voucherNo,
        accountantId: user?.id,
        accountantName: user?.nickName,
        accountedAt: new Date().toISOString(),
        items: raw.items
      })
      if (res.success) {
        message.success('已自动生成并记账')
        loadData()
      } else {
        message.error(res.error || '自动记账失败')
      }
    } catch (error) {
      console.error(error)
      message.error('自动记账失败')
    }
  }

  const formatChargeRange = (item: any) => {
    if (item?.chargeRangeStart && item?.chargeRangeEnd) {
      return String(item.chargeRangeStart) + '~' + String(item.chargeRangeEnd)
    }
    return item?.periodKey || item?.dueDate || ''
  }
  const handlePreviewVoucher = async () => {
    const values = await voucherForm.validateFields()
    const lines: VoucherLine[] = (values.lines || []).map((l: any) => ({
      summary: l.summary,
      subject: l.subject,
      debit: l.debit ? Number(l.debit) : undefined,
      credit: l.credit ? Number(l.credit) : undefined
    }))
    const items = voucherForm.getFieldValue('items') || []
    const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
    const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
    const data: VoucherData = {
      ...values,
      lines,
      totalDebit,
      totalCredit,
      items
    }
    setPreviewData(data)
    setVoucherPreviewVisible(true)
  }

  const openAccountedVoucherPreview = (item: any) => {
    const raw = item?.rawReceipt || item
    let voucher = item?.voucherData || raw?.voucherData
    console.log('[accountant] openAccountedVoucherPreview', { item, raw, voucher })
    if (typeof voucher === 'string') {
      try {
        voucher = JSON.parse(voucher)
      } catch {
        voucher = null
      }
    }
    let lines = voucher?.lines
    if (typeof lines === 'string') {
      try {
        lines = JSON.parse(lines)
      } catch {
        lines = []
      }
    }
    console.log('[accountant] voucher parsed', { voucher, lines })
    if (!voucher || !Array.isArray(lines) || lines.length === 0) {
      message.error('无记账凭证数据')
      return
    }
    const totalDebit = voucher.totalDebit ?? lines.reduce((s: number, l: any) => s + (Number(l.debit) || 0), 0)
    const totalCredit = voucher.totalCredit ?? lines.reduce((s: number, l: any) => s + (Number(l.credit) || 0), 0)
    const receiptItems = Array.isArray(voucher.items) && voucher.items.length
      ? voucher.items
      : getReceiptItems(raw)
    const data: VoucherData = {
      ...voucher,
      voucherNo: voucher.voucherNo || item.voucherNo || raw?.voucherNo || '',
      date: voucher.date || (item.accountedAt ? dayjs(item.accountedAt).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')),
      lines,
      totalDebit,
      totalCredit,
      items: receiptItems
    }
    setPreviewData(data)
    setVoucherPreviewVisible(true)
  }

  const handleSaveVoucher = async () => {
    try {
      const values = await voucherForm.validateFields()
      const lines: VoucherLine[] = (values.lines || []).map((l: any) => ({
        summary: l.summary,
        subject: l.subject,
        debit: l.debit ? Number(l.debit) : undefined,
        credit: l.credit ? Number(l.credit) : undefined
      }))
      const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
      const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
      const voucher: VoucherData = {
        ...values,
        lines,
        totalDebit,
        totalCredit
      }
      const raw = editingReceipt?.rawReceipt || {}
      const res = await simpleDirectCloudApi.updateReceipt(editingReceipt.receiptId, {
        status: 'accounted',
        voucherData: voucher,
        voucherNo: voucher.voucherNo,
        accountantId: user?.id,
        accountantName: user?.nickName,
        accountedAt: new Date().toISOString(),
        items: raw.items
      })
      if (res.success) {
        message.success('凭证保存成功')
        setVoucherModalVisible(false)
        setVoucherPreviewVisible(false)
        setEditingReceipt(null)
        voucherForm.resetFields()
        loadData()
      } else {
        message.error(res.error || '凭证保存失败')
      }
    } catch (error) {
      console.error(error)
      message.error('凭证保存失败')
    }
  }

  const formatMoney = (value: any) => Number(value || 0).toFixed(2)

  const renderReceiptPreview = (items: VoucherItem[], receipt?: any) => {
    if (!items.length) return null
    const rawReceipt = receipt?.rawReceipt || receipt
    const headerItem = items[0]
    const receiptNo = rawReceipt?.receiptId || rawReceipt?._id || rawReceipt?.id || '-'
    const company = rawReceipt?.company || headerItem?.company || rawReceipt?.customerName || '-'
    const date = rawReceipt?.verifiedAt || rawReceipt?.createdAt || rawReceipt?.accountedAt
    const rangeLabel = formatChargeRange(headerItem) || rawReceipt?.periodKey || rawReceipt?.dueDate || ''
    const total = items.reduce((sum, it) => sum + (Number(it.amount ?? 0) || 0), 0)

    return (
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, letterSpacing: 4 }}>
          收款收据
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <div>客户名称: {company}</div>
          <div>No: {receiptNo}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <div>收款范围: {rangeLabel || '-'}</div>
          <div>日期: {date ? dayjs(date).format('YYYY-MM-DD') : '-'}</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #333', padding: 6, width: '10%' }}>货号</th>
              <th style={{ border: '1px solid #333', padding: 6 }}>品名及规格</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '8%' }}>单位</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '8%' }}>数量</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '12%' }}>单价</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '12%' }}>金额</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const amount = Number(it.amount ?? 0) || 0
              const plate = it.plate || rawReceipt?.plate || '-'
              return (
                <tr key={idx}>
                  <td style={{ border: '1px solid #333', padding: 6 }} />
                  <td style={{ border: '1px solid #333', padding: 6 }}>车辆租金（{plate}）</td>
                  <td style={{ border: '1px solid #333', padding: 6 }}>台</td>
                  <td style={{ border: '1px solid #333', padding: 6 }}>1</td>
                  <td style={{ border: '1px solid #333', padding: 6, textAlign: 'right' }}>{formatMoney(amount)}</td>
                  <td style={{ border: '1px solid #333', padding: 6, textAlign: 'right' }}>{formatMoney(amount)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <div>合计人民币: {formatMoney(total)}</div>
          <div>￥{formatMoney(total)}</div>
        </div>
      </div>
    )
  }
  const VoucherPreview: React.FC<{ data: VoucherData }> = ({ data }) => {
    const totalDebit = data.totalDebit ?? data.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
    const totalCredit = data.totalCredit ?? data.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
    const formatCNDate = (value?: string) => {
      const d = value ? dayjs(value) : dayjs()
      return d.isValid() ? d.format('YYYY 年 MM 月 DD 日') : ''
    }
    const pageNo = data.pageNo || '1'
    const totalPages = data.totalPages || '1'

    return (
      <div style={{ background: '#fff', color: '#000', padding: 16, fontFamily: '"SimSun","STSong","Noto Serif SC",serif' }}>
        <div style={{ textAlign: 'center', fontSize: 26, fontWeight: 700, letterSpacing: 6, marginBottom: 8 }}>
          记账凭证
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
          <span>{formatCNDate(data.date)}</span>
          <span>凭证号:{data.voucherNo}</span>
          <span>{pageNo}/{totalPages}</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #000', padding: 6, width: '20%' }}>摘要</th>
              <th style={{ border: '1px solid #000', padding: 6, width: '40%' }}>科目名称</th>
              <th style={{ border: '1px solid #000', padding: 6, width: '20%' }}>借方金额</th>
              <th style={{ border: '1px solid #000', padding: 6, width: '20%' }}>贷方金额</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: 6 }}>{line.summary}</td>
                <td style={{ border: '1px solid #000', padding: 6 }}>{line.subject}</td>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>
                  {line.debit ? formatMoney(line.debit) : ''}
                </td>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>
                  {line.credit ? formatMoney(line.credit) : ''}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ border: '1px solid #000', padding: 6 }}>合计</td>
              <td style={{ border: '1px solid #000', padding: 6 }} />
              <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>{formatMoney(totalDebit)}</td>
              <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>{formatMoney(totalCredit)}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 12 }}>
          <span>附件:{data.attachmentCount || 0} 张</span>
          <span>打印日期:{data.printDate || ''}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12 }}>
          <span>会计主管:{data.bookkeeper || ''}</span>
          <span>记账:{data.accountant || ''}</span>
          <span>出纳:{data.cashier || ''}</span>
          <span>审核:{data.reviewer || ''}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 6, fontSize: 12 }}>
          <span>制证:{data.maker || ''}</span>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
            打印
          </Button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', background: 'var(--bg)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} style={{ backgroundColor: 'var(--positive)' }}>ACC</Avatar>
              <div>
                <Title level={4} style={{ margin: 0 }}>{user?.nickname || user?.nickName || user?.username || 'accountant'}</Title>
                <Text type="secondary">{user?.info || '会计'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
          </Col>
        </Row>
      </Card>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="待记账收款" value={statistics.pendingAccounting} valueStyle={{ color: 'var(--accent)' }} prefix={<CalculatorOutlined />} />
              </Card>
            </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="今日记账" value={statistics.completedToday} valueStyle={{ color: 'var(--positive)' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="累计收款" value={statistics.totalAmount} valueStyle={{ color: 'var(--warning)' }} prefix={<DollarOutlined />} formatter={(v) => `￥${Number(v || 0).toFixed(2)}`} />
          </Card>
        </Col>
      </Row>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space>
              <Text strong>在租车辆应收</Text>
              <Tag color={rentedWithOutstanding.length ? 'orange' : 'default'}>
                {rentedWithOutstanding.length} 台
              </Tag>
              <Text type="secondary">合计</Text>
              <Tag
                color="gold"
                style={{ fontSize: 16, fontWeight: 600, padding: '2px 10px' }}
              >
                ￥{totalOutstanding.toFixed(2)}
              </Tag>
            </Space>
          </Col>
          <Col>
            <Button type="link" onClick={() => setRentedExpanded(prev => !prev)}>
              {rentedExpanded ? '收起' : '查看全部'}
            </Button>
          </Col>
        </Row>
        {rentedExpanded && (
          <List
            itemLayout="vertical"
            dataSource={rentedWithOutstanding}
            renderItem={({ vehicle, outstanding }) => {
              const dueDate = getVehicleDueDate(vehicle)
              return (
                <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                  <List.Item.Meta
                    avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                    title={(
                      <Space>
                        <Text strong>{vehicle.plate}</Text>
                        <Tag color={outstanding > 0 ? 'red' : 'green'}>待收 ￥{outstanding.toFixed(2)}</Tag>
                        <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                      </Space>
                    )}
                    description={(
                      <div>
                        <Text type="secondary">公司：</Text>
                        <Text>{vehicle.company || '-'}</Text>
                        <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                        <Text>{vehicle.contactPerson || '-'}</Text>
                        <Text type="secondary" style={{ marginLeft: 12 }}>租金：</Text>
                        <Text>￥{vehicle.rentAmount || 0}</Text>
                      </div>
                    )}
                  />
                </List.Item>
              )
            }}
          />
        )}
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'pending',
              label: (
                <Badge count={pendingReceipts.length} size="small">
                  <span>待记账收款</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={pendingReceipts}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button key="auto-account" icon={<CheckCircleOutlined />} onClick={() => handleAutoAccount(item)}>
                          一键记账
                        </Button>,
                        <Button key="voucher" type="primary" icon={<FileTextOutlined />} onClick={() => openVoucherModal(item)}>
                          记账
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--accent)' }}>{item.plate}</Avatar>}
                        title={
                          <Space>
                            <Text strong>{item.plate}</Text>
                            <Tag color="blue">待记账</Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">车型</Text> <Text>{item.model}</Text></Col>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">金额</Text> <Text strong style={{ color: 'var(--positive)' }}>￥{item.amount}</Text></Col>
                              <Col span={12}><Text type="secondary">到期日</Text> <Text>{item.reminderDue || item.dueDate || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">提醒周期</Text> <Tag>{getPeriodLabel(item.reminderPeriod, item.reminderPeriodMonths)}</Tag></Col>
                              <Col span={12}><Text type="secondary">提醒金额</Text> <Text>￥{Number(item.reminderAmount ?? item.amount ?? 0).toFixed(2)}</Text></Col>
                            </Row>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )
            },
            {
              key: 'completed',
              label: (
                <Badge count={completedReceipts.length} size="small">
                  <span>已记账收款</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={completedReceipts}
                  renderItem={(item) => (
                    <List.Item actions={[
                        <Button key="accounted-receipt" icon={<FileTextOutlined />} onClick={() => openAccountedVoucherPreview(item)}>查看记账</Button>
                      ]}>
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--positive)' }}>{item.plate}</Avatar>}
                        title={
                          <Space>
                            <Text strong>{item.plate}</Text>
                            <Tag color="green">已记账</Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                              <Col span={12}><Text type="secondary">周期</Text> <Tag>{item.periodKey || '周期'}</Tag></Col>
                            </Row>
                            <Text type="secondary">金额</Text> <Text strong>￥{item.amount}</Text><br />
                            <Text type="secondary">记账时间</Text> <Text>{item.accountedAt ? dayjs(item.accountedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )
            }
          ]}
        />
      </Card>

      <Modal
        title="记账凭证"
        open={voucherModalVisible}
        onCancel={() => { setVoucherModalVisible(false); setVoucherPreviewVisible(false); setEditingReceipt(null); voucherForm.resetFields() }}
        footer={[
          <Button key="preview" icon={<EyeOutlined />} onClick={handlePreviewVoucher}>预览</Button>,
          <Button key="ok" type="primary" icon={<CheckCircleOutlined />} onClick={handleSaveVoucher}>保存</Button>
        ]}
        width={900}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {renderReceiptPreview(watchedItems || [], editingReceipt)}
        <Form form={voucherForm} layout="vertical">
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item label="凭证号" name="voucherNo" rules={[{ required: true, message: '必填' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="日期" name="date" rules={[{ required: true, message: '必填' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="页码" name="pageNo">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="总页数" name="totalPages">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="items" hidden>
            <Input />
          </Form.Item>

          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                <div style={{ background: '#fafafa', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                  <Row gutter={8} style={{ fontSize: 12, fontWeight: 600 }}>
                    <Col span={7}>摘要</Col>
                    <Col span={7}>科目</Col>
                    <Col span={4}>借方金额</Col>
                    <Col span={4}>贷方金额</Col>
                    <Col span={2} />
                  </Row>
                </div>
                {fields.map((field) => (
                  <Row gutter={8} key={field.key} align="middle">
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'summary']}
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input placeholder="摘要" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'subject']}
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input placeholder="科目" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...field} name={[field.name, 'debit']}>
                        <InputNumber style={{ width: '100%' }} placeholder="借方金额" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...field} name={[field.name, 'credit']}>
                        <InputNumber style={{ width: '100%' }} placeholder="贷方金额" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button type="text" icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>
                  添加行
                </Button>
              </>
            )}
          </Form.List>

          <Row gutter={12} style={{ marginTop: 12 }}>
            <Col span={6}>
              <Form.Item label="附件数量" name="attachmentCount">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="会计主管" name="bookkeeper">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="记账" name="accountant">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="出纳" name="cashier">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item label="审核" name="reviewer">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="制证" name="maker">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="打印日期" name="printDate">
                <Input placeholder="2024-01-01" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        open={voucherPreviewVisible}
        footer={null}
        onCancel={() => setVoucherPreviewVisible(false)}
        width={960}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        {previewData && <VoucherPreview data={previewData} />}
      </Modal>

      <Modal
        title="到期提醒"
        open={expiringModalVisible}
        onCancel={() => setExpiringModalVisible(false)}
        footer={null}
        width={720}
      >
        <List
          itemLayout="vertical"
          dataSource={expiringVehiclesAll}
          renderItem={(vehicle) => {
            const dueDate = getVehicleDueDate(vehicle)
            return (
              <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                <List.Item.Meta
                  avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                  title={
                    <Space>
                      <Text strong>{vehicle.plate}</Text>
                      <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                      <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary">公司：</Text>
                      <Text>{vehicle.company || '-'}</Text>
                      <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                      <Text>{vehicle.contactPerson || '-'}</Text>
                    </div>
                  }
                />
              </List.Item>
            )
          }}
        />
      </Modal>
    </div>
  )
}

export default AccountantPage
















